import { createTheme } from '@mui/material/styles';

/**
 * Theme constants for application styling
 * Centralizes all theme variables to ensure consistency
 */

// Project-specific primary colors
export const projectPrimaryColors = {
  'swing-vision': '#0b0b45',
  'q-alpha-flow': '#04CBFF',
  'octopus-sync': '#6D0077',
  'sentinel': '#e863f9',
  default: '#1E52F3'
};

// Function to create theme with custom primary color
export const createCustomTheme = (primaryColor = projectPrimaryColors.default) => createTheme({  palette: {
    primary: {
      main: primaryColor, // Dynamic primary color
      dark: primaryColor === '#1E52F3' ? '#0039cb' : primaryColor, // Use darker shade for default, same for others
      light: `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.05)`,
    },
    common: {
      black: '#000000', // Was 'var(--neutral-black)'
      white: '#FFFFFF', // Was 'var(--neutral-white)'
    },
    text: {
      primary: '#212121', // Was 'var(--neutral-black-light)'
      secondary: '#757575', // Was 'var(--neutral-gray-dark)'
      disabled: '#BDBDBD', // Was 'var(--neutral-gray-light)'
    },
    background: {
      default: '#F5F5F5', // Was 'var(--neutral-white-light)'
      paper: '#FFFFFF',       // Was 'var(--neutral-white)'
    },
    neutral: {
      black: '#000000', // Was 'var(--neutral-black)'
      white: '#FFFFFF', // Was 'var(--neutral-white)'
      blackLight: '#212121', // Was 'var(--neutral-black-light)'
      blackLighter: '#424242', // Placeholder for 'var(--neutral-black-lighter)'
      whiteLight: '#F5F5F5', // Was 'var(--neutral-white-light)'
      whiteLighter: '#E0E0E0', // Placeholder for 'var(--neutral-white-lighter)'
      gray: '#9E9E9E', // Placeholder for 'var(--neutral-gray)'
      grayLight: '#BDBDBD', // Was 'var(--neutral-gray-light)'
      grayDark: '#757575', // Was 'var(--neutral-gray-dark)'
      lightGray: '#EEEEEE', // Placeholder for 'var(--neutral-light-gray)'
    },    action: { // Added placeholder, ensure these are your desired values
      hover: `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.08)`, 
      selected: `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.16)`,
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      focus: `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.12)`,
    }
  },
  typography: {
    fontFamily: 'Manrope, "Nunito Sans", sans-serif',
    h1: {
      fontFamily: 'Manrope, "Nunito Sans", sans-serif',
      fontWeight: 700, // Was 'var(--font-weight-bold)'
      fontSize: '2.5rem', // Was 'var(--font-size-4xl)'
      lineHeight: 1.2, // Was 'var(--line-height-tight)'
    },
    h2: {
      fontFamily: 'Manrope, "Nunito Sans", sans-serif',
      fontWeight: 700, // Was 'var(--font-weight-bold)'
      fontSize: '2rem', // Was 'var(--font-size-3xl)'
      lineHeight: 1.2, // Was 'var(--line-height-tight)'
    },
    h3: {
      fontFamily: 'Manrope, "Nunito Sans", sans-serif',
      fontWeight: 600, // Was 'var(--font-weight-semibold)'
      fontSize: '1.5rem', // Was 'var(--font-size-2xl)'
      lineHeight: 1.2, // Was 'var(--line-height-tight)'
    },
    h4: {
      fontFamily: 'Manrope, "Nunito Sans", sans-serif',
      fontWeight: 600, // Was 'var(--font-weight-semibold)'
      fontSize: '1.25rem', // Was 'var(--font-size-xl)'
      lineHeight: 1.5, // Was 'var(--line-height-normal)'
    },
    h5: {
      fontFamily: 'Manrope, "Nunito Sans", sans-serif',
      fontWeight: 500, // Was 'var(--font-weight-medium)'
      fontSize: '1.125rem', // Was 'var(--font-size-lg)'
      lineHeight: 1.5, // Was 'var(--line-height-normal)'
    },
    h6: {
      fontFamily: 'Manrope, "Nunito Sans", sans-serif',
      fontWeight: 500, // Was 'var(--font-weight-medium)'
      fontSize: '1rem', // Was 'var(--font-size-base)'
      lineHeight: 1.5, // Was 'var(--line-height-normal)'
    },
    subtitle1: {
      fontFamily: '"Nunito Sans", Manrope, sans-serif',
      fontWeight: 600, // Was 'var(--font-weight-semibold)'
      fontSize: '1rem', // Was 'var(--font-size-base)'
      lineHeight: 1.75, // Was 'var(--line-height-relaxed)'
    },
    subtitle2: {
      fontFamily: '"Nunito Sans", Manrope, sans-serif',
      fontWeight: 500, // Was 'var(--font-weight-medium)'
      fontSize: '0.875rem', // Was 'var(--font-size-sm)'
      lineHeight: 1.75, // Was 'var(--line-height-relaxed)'
    },
    body1: {
      fontFamily: '"Nunito Sans", Manrope, sans-serif',
      fontWeight: 400, // Was 'var(--font-weight-regular)'
      fontSize: '1rem', // Was 'var(--font-size-base)'
      lineHeight: 1.5, // Was 'var(--line-height-normal)'
    },
    body2: {
      fontFamily: '"Nunito Sans", Manrope, sans-serif',
      fontWeight: 400, // Was 'var(--font-weight-regular)'
      fontSize: '0.875rem', // Was 'var(--font-size-sm)'
      lineHeight: 1.5, // Was 'var(--line-height-normal)'
    },
    button: {
      fontFamily: 'Manrope, "Nunito Sans", sans-serif',
      fontWeight: 500, // Was 'var(--font-weight-medium)'
      fontSize: '0.875rem', // Was 'var(--font-size-sm)'
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"Nunito Sans", Manrope, sans-serif',
      fontWeight: 400, // Was 'var(--font-weight-regular)'
      fontSize: '0.75rem', // Was 'var(--font-size-xs)'
      lineHeight: 1.5, // Was 'var(--line-height-normal)'
    },
    overline: {
      fontFamily: 'Manrope, "Nunito Sans", sans-serif',
      fontWeight: 600, // Was 'var(--font-weight-semibold)'
      fontSize: '0.75rem', // Was 'var(--font-size-xs)'
      textTransform: 'uppercase',
      lineHeight: 1.5, // Was 'var(--line-height-normal)'
    },
    fontSizes: { // Direct values instead of vars
        xs: '0.75rem',
        small: '0.875rem',
        medium: '1rem',
        large: '1.125rem',
        xl: '1.25rem',
        xxl: '1.5rem',
        xxxl: '2rem',
        xxxxl: '2.5rem',
        xxxxxl: '3rem', // Placeholder for 'var(--font-size-5xl)'
    },
    fontWeights: { // Direct values instead of vars
        light: 300, // Was 'var(--font-weight-light)'
        regular: 400, // Was 'var(--font-weight-regular)'
        medium: 500, // Was 'var(--font-weight-medium)'
        semiBold: 600, // Was 'var(--font-weight-semibold)'
        bold: 700, // Was 'var(--font-weight-bold)'
        extraBold: 800, // Was 'var(--font-weight-extrabold)'
    }
  },
  spacing: 8, // Base spacing unit, 8px. theme.spacing(1) = 8px, theme.spacing(2) = 16px etc.
  breakpoints: {
    values: { // From variable.css (implicitly, standard values)
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  shape: {
    borderRadius: 8, // Was 'var(--border-radius-md)'
    borderRadiusSmall: 4, // Was 'var(--border-radius-sm)'
    borderRadiusLarge: 16, // Was 'var(--border-radius-lg)'
    borderRadiusFull: 9999, // Was 'var(--border-radius-full)'
  },
  shadows: [ 
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)', // MUI default 1
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)', // MUI default 2
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)', // MUI default 3
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)', // MUI default 4
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)', // MUI default 5
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)', // MUI default 6
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)', // MUI default 7
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)', // MUI default 8
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)', // MUI default 9
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)', // MUI default 10
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)', // MUI default 11
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)', // MUI default 12
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)', // MUI default 13
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)', // MUI default 14
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)', // MUI default 15
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)', // MUI default 16
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)', // MUI default 17
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)', // MUI default 18
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)', // MUI default 19
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)', // MUI default 20
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)', // MUI default 21
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)', // MUI default 22
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)', // MUI default 23
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)', // MUI default 24
  ],
  transitions: {
    duration: {
      shortest: 150, // MUI default
      shorter: 200, // MUI default
      short: 250, // MUI default, maps to 'var(--transition-fast)' which is 0.2s
      standard: 300, // MUI default, maps to 'var(--transition-normal)' which is 0.3s
      complex: 375, // MUI default
      enteringScreen: 225, // MUI default
      leavingScreen: 195, // MUI default
    },
    easing: { // MUI defaults, can be customized
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    // Custom direct mappings if needed, though MUI components use the above structure
    customDurations: {
        fast: '0.2s', // Was 'var(--transition-fast)'
        normal: '0.3s', // Was 'var(--transition-normal)'
        slow: '0.5s', // Was 'var(--transition-slow)'
    }
  },
  zIndex: { // Mapping to MUI standard keys where possible, or using custom
    mobileStepper: 1000, 
    fab: 1050,          
    speedDial: 1050,    
    appBar: 1100,       
    drawer: 1200,       
    modal: 1300,        
    snackbar: 1400,     
    tooltip: 1500,      
    customDropdown: 1000, // Was 'var(--z-index-dropdown)'
    customSticky: 1020,   // Was 'var(--z-index-sticky)'
    customFixed: 1030,    // Was 'var(--z-index-fixed)'
    customPopover: 1060,  // Was 'var(--z-index-popover)'
  },
  // Global component style overrides
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        /* Manrope Font Faces */
        @font-face {
          font-family: 'Manrope';
          font-style: normal;
          font-display: swap;
          font-weight: 200;
          src: url('/src/assets/fonts/Manrope/Manrope-ExtraLight.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Manrope';
          font-style: normal;
          font-display: swap;
          font-weight: 300;
          src: url('/src/assets/fonts/Manrope/Manrope-Light.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Manrope';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: url('/src/assets/fonts/Manrope/Manrope-Regular.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Manrope';
          font-style: normal;
          font-display: swap;
          font-weight: 500;
          src: url('/src/assets/fonts/Manrope/Manrope-Medium.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Manrope';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
          src: url('/src/assets/fonts/Manrope/Manrope-SemiBold.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Manrope';
          font-style: normal;
          font-display: swap;
          font-weight: 700;
          src: url('/src/assets/fonts/Manrope/Manrope-Bold.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Manrope';
          font-style: normal;
          font-display: swap;
          font-weight: 800;
          src: url('/src/assets/fonts/Manrope/Manrope-ExtraBold.ttf') format('truetype');
        }

        /* Nunito Sans Font Faces */
        @font-face {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 200;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-ExtraLight.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: italic;
          font-display: swap;
          font-weight: 200;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-ExtraLightItalic.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 300;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-Light.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: italic;
          font-display: swap;
          font-weight: 300;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-LightItalic.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-Regular.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: italic;
          font-display: swap;
          font-weight: 400;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-Italic.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-SemiBold.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: italic;
          font-display: swap;
          font-weight: 600;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-SemiBoldItalic.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 700;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-Bold.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: italic;
          font-display: swap;
          font-weight: 700;
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-BoldItalic.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 800; /* Corresponds to ExtraBold */
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-ExtraBold.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: italic;
          font-display: swap;
          font-weight: 800; /* Corresponds to ExtraBold */
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-ExtraBoldItalic.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 900; /* Corresponds to Black */
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-Black.ttf') format('truetype');
        }
        @font-face {
          font-family: 'Nunito Sans';
          font-style: italic;
          font-display: swap;
          font-weight: 900; /* Corresponds to Black */
          src: url('/src/assets/fonts/Nunito_Sans/NunitoSans-BlackItalic.ttf') format('truetype');
        }
        // Add other global styles here if needed
      `,
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true, // Example: common button style
      },
      styleOverrides: {
        root: {
          textTransform: 'none', // Already set in typography.button but good for explicitness
          // Add other global button styles here using theme.spacing, theme.palette etc.
        },
      },
    },
    MuiLink: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                    textDecoration: 'underline',
                },
            }),
        }
    }    // Add other global component overrides here
    // e.g., MuiAppBar, MuiCard, MuiTextField, etc.
  },
});

// Default theme instance for backward compatibility
const muiTheme = createCustomTheme();

export default muiTheme;
